import { useEffect, useState } from "react";
import { normalizeQuestions, Questionnaire } from "../../types/Questionnaire";
import DB_PATH from "../DB_PATH";
import { firestore } from "../firebase";

type Options = {
  communityId?: string;
};

export const useQuestionnaire = ({ communityId }: Options) => {
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!communityId) return;

    setLoading(true);
    const ref = firestore
      .collection(DB_PATH.COMMUNITY)
      .doc(communityId)
      .collection(DB_PATH.ONBOARDING)
      .doc("questionnaire_metadata");

    const unsubscribe = ref.onSnapshot(
      (doc) => {
        if (!doc.exists) {
          setQuestionnaire(null);
          setLoading(false);
          return;
        }

        const data = doc.data() as Questionnaire;
        setQuestionnaire({
          ...data,
          questions: normalizeQuestions((data as any)?.questions),
        } as Questionnaire);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching questionnaire", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [communityId]);

  return { questionnaire, loading };
};

export const saveQuestionnaire = async ({
  communityId,
  questionnaire,
}: {
  communityId: string;
  questionnaire: Questionnaire;
}): Promise<void> => {
  if (!communityId) throw new Error("communityId is required");

  try {
    const docRef = firestore
      .collection(DB_PATH.COMMUNITY)
      .doc(communityId)
      .collection(DB_PATH.ONBOARDING)
      .doc("questionnaire_metadata");
    await docRef.set({
      ...questionnaire,
      questions: normalizeQuestions((questionnaire as any)?.questions),
    });
  } catch (error) {
    console.error("Error saving questionnaire", error);
    throw error;
  }
};

export const removeQuestionnaire = async (
  communityId: string
): Promise<void> => {
  if (!communityId) return;

  try {
    const docRef = firestore
      .collection(DB_PATH.COMMUNITY)
      .doc(communityId)
      .collection(DB_PATH.ONBOARDING)
      .doc("questionnaire_metadata");
    await docRef.delete();
  } catch (error) {
    console.error("Error removing questionnaire", error);
  }
};
